import { Avatar, AvatarFallback, Input } from '@repo/ui'
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
} from '@tanstack/react-router'
import { Loader2Icon, SearchIcon, UsersIcon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { AppHeader } from '@/components/app-header'
import { useAuth } from '@/hooks/use-auth'
import { useContacts } from '@/hooks/use-contacts'
import { useOnlineStatus } from '@/hooks/use-online-status'
import { getInitials } from '@/lib/format'

export const Route = createFileRoute('/_app/contatos')({
  component: ContatosPage,
})

function ContatosPage() {
  const { currentOrgId } = useAuth()
  const online = useOnlineStatus()
  const location = useLocation()
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 300)
    return () => clearTimeout(id)
  }, [searchInput])

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useContacts(currentOrgId, search)

  const rows = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data]
  )

  const sentinelRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const node = sentinelRef.current
    if (!node) return
    const observer = new IntersectionObserver((entries) => {
      if (
        online &&
        entries[0]?.isIntersecting &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        void fetchNextPage()
      }
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, online])

  if (location.pathname !== '/contatos') {
    return <Outlet />
  }

  return (
    <div className="lg:border-border flex min-h-0 flex-1 flex-col lg:mx-auto lg:w-full lg:max-w-2xl lg:border-x">
      <AppHeader title="Contatos" />

      <div className="border-border bg-card shrink-0 border-b px-3 pb-2">
        <div className="relative">
          <SearchIcon className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar contato"
            className="h-10 pl-9"
            type="search"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2Icon className="text-muted-foreground size-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-muted-foreground flex flex-col items-center justify-center gap-2 py-20 text-center">
            <UsersIcon className="size-10 opacity-40" />
            <p className="text-sm">Nenhum contato encontrado</p>
          </div>
        ) : (
          <div className="divide-border divide-y">
            {rows.map((contact) => (
              <Link
                key={contact.id}
                to="/contatos/$contactId"
                params={{ contactId: contact.id }}
                className="flex items-center gap-3 px-3 py-3"
              >
                <Avatar className="size-11 shrink-0">
                  <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                    {getInitials(contact.name ?? contact.phone)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {contact.name ?? contact.phone ?? 'Sem nome'}
                  </p>
                  <p className="text-muted-foreground truncate text-sm">
                    {contact.phone ?? contact.email ?? ''}
                  </p>
                </div>
                {contact.tags.length > 0 ? (
                  <span
                    className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium"
                    style={{
                      backgroundColor: `${contact.tags[0]!.color}22`,
                      color: contact.tags[0]!.color,
                    }}
                  >
                    {contact.tags[0]!.name}
                  </span>
                ) : null}
              </Link>
            ))}
            <div ref={sentinelRef} className="h-8" />
            {isFetchingNextPage ? (
              <div className="flex justify-center py-4">
                <Loader2Icon className="text-muted-foreground size-5 animate-spin" />
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}
